import { useState, useEffect } from 'react';
import { ChevronDown, Search, Check, X } from 'lucide-react';
import { COUNTRIES } from '../lib/countries';

interface CountrySelectProps {
  value: string;
  onChange: (country: string) => void;
  placeholder?: string;
}

export default function CountrySelect({ value, onChange, placeholder = 'Select your country' }: CountrySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getCountryMatch = (country: string) => {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    const normalizedCountry = country.toLowerCase();
    return normalizedSearch === '' || normalizedCountry.includes(normalizedSearch);
  };

  const matchingCount = COUNTRIES.filter(country => getCountryMatch(country)).length;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSelect = (country: string) => {
    onChange(country);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="w-full bg-black/30 text-text-primary rounded-lg px-3.5 py-2.5 text-[15px] border border-[#282828] hover:border-[#383838] focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/15 focus:outline-none transition-all text-left flex items-center justify-between"
      >
        <span className={value ? 'text-text-primary' : 'text-[#707070]'}>
          {value || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-[#50B3A8]" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 animate-in fade-in duration-200"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
            onClick={handleClose}
          />

          {/* Modal - Fixed position container */}
          <div
            className="fixed inset-x-0 bottom-0 md:inset-0 z-50 flex items-end md:items-start justify-center p-0 md:p-4"
            style={{
              paddingBottom: 'calc(88px + env(safe-area-inset-bottom))',
              paddingTop: 'max(10vh, 80px)',
            }}
          >
            <div
              className="bg-[#181818] border border-[#282828] w-full md:max-w-md md:rounded-[20px] rounded-t-[20px] overflow-hidden animate-in slide-in-from-bottom md:zoom-in-95 duration-300 flex flex-col"
              style={{
                maxHeight: 'min(70vh, 560px)',
                boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)',
              }}
            >
              {/* Header - Fixed */}
              <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-stroke">
                <h2 className="text-[18px] font-semibold text-text-primary">
                  Select country
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-text-secondary" />
                </button>
              </div>

              {/* Search - Fixed */}
              <div className="flex-shrink-0 p-4 border-b border-stroke">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search countries..."
                    autoFocus
                    className="w-full bg-transparent text-text-primary rounded-[12px] pl-11 pr-4 py-3 text-[15px] border border-stroke focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/15 focus:outline-none transition-colors duration-200"
                  />
                </div>
              </div>

              {/* Results - Scrollable with fixed height on mobile */}
              <div className="overflow-y-auto h-[50vh] md:h-auto">
                {matchingCount > 0 ? (
                  <div className="divide-y divide-stroke">
                    {COUNTRIES.filter(country => getCountryMatch(country)).map((country) => {
                      const isSelected = value === country;

                      return (
                        <button
                          key={country}
                          type="button"
                          onClick={() => handleSelect(country)}
                          className={`w-full px-6 py-4 text-left text-[15px] flex items-center justify-between hover:bg-white/5 active:bg-white/10 cursor-pointer transition-colors ${
                            isSelected ? 'bg-brand-teal/10' : ''
                          }`}
                        >
                          <span className={isSelected ? 'text-brand-teal font-medium' : 'text-text-primary'}>
                            {country}
                          </span>
                          {isSelected && (
                            <Check className="w-5 h-5 text-brand-teal flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center px-6">
                      <p className="text-[15px] text-text-secondary">No countries found</p>
                      <p className="text-[13px] text-text-tertiary mt-2">Try a different search term</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
